"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { clog } from "@/lib/client-log";
import EliminatedNameModal from "./eliminated-name-modal";

interface SearchResult {
  id: string;
  name: string;
  eliminated: boolean;
  resurrected: boolean;
  lcKEligible: boolean;
  status: string;
  winCount: number;
  lossCount: number;
  lcKVoteCount: number;
  alreadyVotedByMe: boolean;
}

const DEBOUNCE_MS = 250;

export default function NameSearchModal({ onClose }: { onClose: () => void }) {
  const router = useRouter();
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [lckRequired, setLckRequired] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/name-search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setResults(data.results ?? []);
        setLckRequired(data.lckRequired ?? 0);
      } catch (err) {
        clog.warn("name-search", "search fetch failed", { error: String(err) });
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  function handleSelect(r: SearchResult) {
    const isEliminated = r.eliminated && !r.resurrected;
    if (isEliminated) {
      clog.info("name-search", "opened eliminated name from search", { id: r.id });
      setSelected(r);
    } else {
      // Active names already have full detail + futures-betting UI on the
      // roster page — deep-link there instead of duplicating that UI here.
      clog.info("name-search", "navigating to roster card from search", { id: r.id });
      router.push(`/names?highlight=${r.id}`);
      onClose();
    }
  }

  if (selected) {
    return (
      <EliminatedNameModal
        name={{
          id: selected.id,
          name: selected.name,
          origin: null,
          meaning: null,
          winCount: selected.winCount,
          lossCount: selected.lossCount,
          tieCount: 0,
          lcKVoteCount: selected.lcKVoteCount,
          resurrected: selected.resurrected,
          lcKEligible: selected.lcKEligible,
          lckOpen: true,
        }}
        lckRequired={lckRequired}
        alreadyVoted={selected.alreadyVotedByMe}
        onClose={() => {
          // Same "refresh whatever page is underneath on exit" behavior as
          // the roster page's NameCardGrid — search can be opened from any
          // page via the navbar, so this is the only place that can trigger it.
          router.refresh();
          onClose();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20"
      style={{ background: "rgba(15,5,32,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-2 border-b border-purple-50">
          <div className="flex items-center gap-2">
            <span className="text-purple-300 text-lg">🔍</span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a name…"
              className="flex-1 text-lg font-semibold text-purple-900 placeholder-purple-300 outline-none bg-transparent"
            />
            <button
              onClick={onClose}
              className="text-purple-400 hover:text-purple-700 text-xl leading-none select-none"
              style={{ WebkitUserSelect: "none", userSelect: "none" }}
              aria-label="Close"
            >✕</button>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-purple-300">Searching…</div>
          )}

          {!loading && query.trim() && results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-purple-300">No names match &ldquo;{query}&rdquo;</div>
          )}

          {!loading && results.map((r) => {
            const isEliminated = r.eliminated && !r.resurrected;
            return (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-purple-50 transition border-b border-purple-50 last:border-0"
              >
                <span className={`font-bold text-sm flex-1 truncate ${isEliminated ? "text-gray-400 line-through" : "text-purple-800"}`}>
                  {r.name}
                </span>
                {r.status === "RESURRECTED" && <span className="text-xs">🔥</span>}
                {isEliminated && r.lcKEligible && <span className="text-xs">🍳</span>}
                <span className="text-[10px] text-purple-300 shrink-0">
                  {isEliminated ? "Eliminated" : "Active"}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
